import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Paper,
    Popper,
    Grow,
    ClickAwayListener,
    Typography,
    IconButton,
    useTheme,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { getStyles } from '../styles';

// AI Action Types
export const AI_ACTIONS = {
    // Reply category
    SMART_REPLY: 'SMART_REPLY',
    APPOINTMENT_REMINDER: 'APPOINTMENT_REMINDER',
    FOLLOW_UP: 'FOLLOW_UP',
    HANDLE_CONCERN: 'HANDLE_CONCERN',
    // Summarize category
    SUMMARIZE_CHAT: 'SUMMARIZE_CHAT',
    PAIN_POINTS: 'PAIN_POINTS',
    ACTION_ITEMS: 'ACTION_ITEMS',
    // Compose category
    EXPLAIN_TREATMENT: 'EXPLAIN_TREATMENT',
    PRICING_INFO: 'PRICING_INFO',
    // Utility category
    IMPROVE_TONE: 'IMPROVE_TONE',
    TRANSLATE: 'TRANSLATE',
};

// Menu items configuration
const MENU_CATEGORIES = [
    {
        category: 'Reply',
        icon: 'mdi:message-reply-text-outline',
        color: '#00A76F',
        items: [
            {
                action: AI_ACTIONS.SMART_REPLY,
                label: 'Smart Reply',
                description: 'Generate contextual response',
                icon: 'mdi:robot-outline',
            },
            {
                action: AI_ACTIONS.APPOINTMENT_REMINDER,
                label: 'Appointment Reminder',
                description: 'Create personalized reminder',
                icon: 'mdi:calendar-clock',
            },
            {
                action: AI_ACTIONS.FOLLOW_UP,
                label: 'Follow-up Message',
                description: 'Draft a follow-up',
                icon: 'mdi:message-arrow-right-outline',
            },
            {
                action: AI_ACTIONS.HANDLE_CONCERN,
                label: 'Handle Concern',
                description: 'Empathetic response',
                icon: 'mdi:hand-heart-outline',
            },
        ],
    },
    {
        category: 'Summarize',
        icon: 'mdi:text-box-outline',
        color: '#2065D1',
        items: [
            {
                action: AI_ACTIONS.SUMMARIZE_CHAT,
                label: 'Conversation Summary',
                description: 'Key points & timeline',
                icon: 'mdi:format-list-bulleted',
            },
            {
                action: AI_ACTIONS.PAIN_POINTS,
                label: 'Patient Pain Points',
                description: 'Concerns & needs',
                icon: 'mdi:alert-circle-outline',
            },
            {
                action: AI_ACTIONS.ACTION_ITEMS,
                label: 'Action Items',
                description: 'Tasks & next steps',
                icon: 'mdi:checkbox-marked-outline',
            },
        ],
    },
    {
        category: 'Compose',
        icon: 'mdi:pencil-outline',
        color: '#B76E00',
        items: [
            {
                action: AI_ACTIONS.EXPLAIN_TREATMENT,
                label: 'Explain Treatment',
                description: 'Patient-friendly explanation',
                icon: 'mdi:tooth-outline',
            },
            {
                action: AI_ACTIONS.PRICING_INFO,
                label: 'Send Pricing Info',
                description: 'Professional pricing message',
                icon: 'mdi:currency-gbp',
            },
        ],
    },
    {
        category: 'Utility',
        icon: 'mdi:wrench-outline',
        color: '#637381',
        items: [
            {
                action: AI_ACTIONS.IMPROVE_TONE,
                label: 'Improve Tone',
                description: 'Make message professional',
                icon: 'mdi:format-text',
            },
            {
                action: AI_ACTIONS.TRANSLATE,
                label: 'Translate Message',
                description: 'Translate to another language',
                icon: 'mdi:translate',
            },
        ],
    },
];

// Translation languages
const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Spanish' },
    { code: 'ar', label: 'Arabic' },
    { code: 'hi', label: 'Hindi' },
    { code: 'pl', label: 'Polish' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
];

const AIAssistantMenu = ({
    anchorEl,
    open,
    onClose,
    onSelectAction,
    loading,
    isMobile,
}) => {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [showLanguages, setShowLanguages] = React.useState(false);

    const handleItemClick = (action) => {
        if (action === AI_ACTIONS.TRANSLATE) {
            setShowLanguages(true);
        } else {
            onSelectAction(action);
            onClose();
        }
    };

    const handleLanguageSelect = (languageLabel) => {
        onSelectAction(AI_ACTIONS.TRANSLATE, { targetLanguage: languageLabel });
        setShowLanguages(false);
        onClose();
    };

    const handleClose = () => {
        setShowLanguages(false);
        onClose();
    };

    // Shared menu body — used by both mobile bottom sheet and desktop Popper
    const menuContent = (
        <Box>
            {/* Header */}
            <Box sx={styles.aiMenuHeader}>
                <Box sx={styles.aiMenuTitle}>
                    <Icon icon="mdi:sparkles" width={18} height={18} style={{ color: '#00A76F' }} />
                    <Typography component="span">AI Assistant</Typography>
                </Box>
                <IconButton size="small" onClick={handleClose} sx={{ width: 24, height: 24 }}>
                    <Icon icon="mdi:close" width={16} height={16} />
                </IconButton>
            </Box>

            {/* Language Selection (when Translate is clicked) */}
            {showLanguages ? (
                <Box>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        pb: 1,
                        cursor: 'pointer',
                    }}
                        onClick={() => setShowLanguages(false)}
                    >
                        <Icon icon="mdi:arrow-left" width={16} height={16} />
                        <Typography sx={{ fontSize: '12px', color: '#637381' }}>
                            Back to menu
                        </Typography>
                    </Box>
                    <Typography sx={styles.aiMenuCategory}>
                        Select Language
                    </Typography>
                    <Box sx={styles.aiLanguagePicker}>
                        {LANGUAGES.map((lang) => (
                            <Box
                                key={lang.code}
                                component="button"
                                sx={styles.aiLanguageBtn}
                                onClick={() => handleLanguageSelect(lang.label)}
                            >
                                {lang.label}
                            </Box>
                        ))}
                    </Box>
                </Box>
            ) : (
                /* Main Menu */
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {MENU_CATEGORIES.map((category) => (
                        <Box key={category.category}>
                            <Typography sx={styles.aiMenuCategory}>
                                {category.category}
                            </Typography>
                            {category.items.map((item) => (
                                <Box
                                    key={item.action}
                                    sx={{
                                        ...styles.aiMenuItem,
                                        opacity: loading ? 0.5 : 1,
                                        pointerEvents: loading ? 'none' : 'auto',
                                    }}
                                    onClick={() => handleItemClick(item.action)}
                                >
                                    <Box
                                        sx={{
                                            ...styles.aiMenuItemIcon,
                                            backgroundColor: `${category.color}16`,
                                            color: category.color,
                                        }}
                                    >
                                        <Icon icon={item.icon} width={18} height={18} />
                                    </Box>
                                    <Box sx={styles.aiMenuItemText}>
                                        <Typography sx={styles.aiMenuItemLabel}>
                                            {item.label}
                                        </Typography>
                                        <Typography sx={styles.aiMenuItemDesc}>
                                            {item.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );

    // Mobile: render as bottom sheet
    if (isMobile && open) {
        return (
            <>
                <Box
                    onClick={onClose}
                    sx={{ position: 'fixed', inset: 0, zIndex: 1299, backgroundColor: 'rgba(0,0,0,0.5)' }}
                />
                <Paper
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1300,
                        borderRadius: '16px 16px 0 0',
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        padding: '8px',
                        maxWidth: '100%',
                        minWidth: '100%',
                    }}
                >
                    {menuContent}
                </Paper>
            </>
        );
    }

    // Desktop: render as anchored Popper
    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="top-end"
            transition
            style={{ zIndex: 1300 }}
            modifiers={[
                { name: 'offset', options: { offset: [0, 8] } },
                { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
            ]}
        >
            {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                    <Paper sx={styles.aiMenu}>
                        <ClickAwayListener onClickAway={handleClose}>
                            {menuContent}
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );
};

AIAssistantMenu.propTypes = {
    anchorEl: PropTypes.object,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelectAction: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    isMobile: PropTypes.bool,
};

AIAssistantMenu.defaultProps = {
    anchorEl: null,
    loading: false,
    isMobile: false,
};

export default AIAssistantMenu;
export { MENU_CATEGORIES };
